/* eslint-disable react-hooks/exhaustive-deps */
import { DownOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Select, Switch, Tooltip } from 'antd';
import { SelectValue } from 'antd/lib/select';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import AlphabetButton from 'components/UI/AlphabetButton/AlphabetButton';
import { useBreakpoints } from 'hooks/useMediaQuery';
import {
  getLetterSelectedProductList,
  getModePagination,
  getOrderByProductList,
  setLetterSelectedProductList,
  setModePagination,
  setOrderByProductList,
  setPageProductList,
} from 'services/config';
import { useGetProductCategoriesQuery } from 'services/ecommerceApi';
import { offerFiltersSelectors } from 'store/state/selectors';
import { offerFiltersActions, productsActions } from 'store/state/slices';
import { getChannelName } from 'utils/channelUtils';

import * as S from './styles';

const { Option } = Select;

function ListControlsBar() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isNotDesktop } = useBreakpoints();
  const offerFilters = useSelector(offerFiltersSelectors.getOfferFilters);
  const { data: productCategories = [] } = useGetProductCategoriesQuery({});
  const selectedChannel = useMemo(() => {
    const { offerTypes = [], categories = [] } = offerFilters;

    return getChannelName(offerTypes, categories, productCategories, location.pathname);
  }, [offerFilters, productCategories, location.pathname]);

  const [orderBy, setOrderBy] = useState<string>(
    getOrderByProductList() || 'AZ'
  );

  const [isInfiniteList, setIsInfiniteList] = useState<boolean>(
    getModePagination() === 'false',
  );
  const [selectedLetter, setSelectedLetter] = useState<string | null>(
    getLetterSelectedProductList() || null,
  );
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (isInfiniteList) {
      dispatch(productsActions.setInfinity(true));
    }
  }, []);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleChangeOrder = useCallback(
    (orderFilter: SelectValue) => {
      const newOrderBy = orderFilter as string;

      setOrderByProductList(newOrderBy);
      setPageProductList(1);

      setOrderBy(newOrderBy);

      dispatch(
        offerFiltersActions.filter({
          ...offerFilters,
          orderBy: newOrderBy,
        }),
      );
    },
    [dispatch, offerFilters],
  );

  const handleSwitchChange = useCallback(
    (checked: boolean) => {
      setModePagination(!checked);
      setPageProductList(1);

      setIsInfiniteList(checked);

      dispatch(productsActions.setInfinity(checked));
    },
    [dispatch],
  );

  const handleLetterSelect = useCallback(
    (letter: string | null) => {
      setSelectedLetter(letter);
      setLetterSelectedProductList(letter || '');
      setPageProductList(1);

      dispatch(
        offerFiltersActions.filter({
          ...offerFilters,
          initial: letter || undefined,
        }),
      );
    },
    [dispatch, offerFilters],
  );

  const showBody = !isNotDesktop || isExpanded;

  return (
    <S.Container className="list-controls-bar">
      <S.Content>
        <S.Header
          $isMobile={isNotDesktop}
          $isExpanded={isExpanded}
          onClick={isNotDesktop ? toggleExpanded : undefined}
          aria-expanded={isNotDesktop ? isExpanded : undefined}
          tabIndex={isNotDesktop ? 0 : undefined}
        >
          <S.TitleGroup>
            <S.Title>{selectedChannel}</S.Title>
            <S.Subtitle>Filtrar por letra</S.Subtitle>
          </S.TitleGroup>
          <S.ChevronIcon $isExpanded={isExpanded}>
            <DownOutlined />
          </S.ChevronIcon>
        </S.Header>

        <S.ControlsWrapper $isMobile={isNotDesktop} $isVisible={showBody}>
          <S.ControlsGroup>
            <S.TooltipWrapper>
              <Tooltip
                title="Os valores exibidos não incluem o ICMS"
                placement="left"
              >
                <QuestionCircleOutlined />
              </Tooltip>
              <S.TooltipText>
                Os valores exibidos <strong>não</strong> incluem o ICMS
              </S.TooltipText>
            </S.TooltipWrapper>

            {!isNotDesktop && (
              <>
                <S.Divider />

                <S.SwitchWrapper>
                  <Switch
                    size="small"
                    checked={isInfiniteList}
                    onChange={handleSwitchChange}
                  />
                  <S.SwitchLabel>Lista Infinita</S.SwitchLabel>
                </S.SwitchWrapper>
              </>
            )}

            <S.Divider />

            <S.SelectWrapper>
              <S.SelectLabel>Ordenar</S.SelectLabel>
              <Select
                value={orderBy}
                onChange={handleChangeOrder}
                size="small"
                suffixIcon={<DownOutlined />}
                bordered={false}
              >
                <Option value="AZ">A-Z</Option>
                <Option value="ZA">Z-A</Option>
              </Select>
            </S.SelectWrapper>
          </S.ControlsGroup>

          <S.AlphabetWrapper>
            <AlphabetButton
              selectedLetter={selectedLetter}
              onLetterSelect={handleLetterSelect}
            />
          </S.AlphabetWrapper>
        </S.ControlsWrapper>
      </S.Content>
    </S.Container>
  );
}

export default ListControlsBar;
